import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M10 11v6', 'M14 11v6'],
    arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7']
};

export default function PettyCashSetup({ onBack }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const categories = [
        { key: 'petty-cash-account-names', label: 'PETTY CASH ACCOUNT NAMES', placeholder: 'e.g. Office Petty Cash' },
        { key: 'petty-cash-locations', label: 'PETTY CASH LOCATIONS', placeholder: 'e.g. Main Office, Workshop' },
        { key: 'petty-cash-transaction-types', label: 'PETTY CASH TRANSACTION TYPES', placeholder: 'e.g. Fund Addition, Withdrawal' },
        { key: 'petty-cash-description-presets', label: 'DESCRIPTION PRESETS', placeholder: 'e.g. Fuel - Emarat, Office Supplies' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/master-data');
            setData(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    return (
        <div className="md-page">
            <header className="md-header">
                <div>
                    <h1 className="md-title">Petty Cash Configuration</h1>
                    <p className="md-subtitle">Manage standardized account names and locations for fund tracking.</p>
                </div>
                <button className="premium-btn btn-secondary" onClick={onBack}>
                    <Icon d={icons.arrowLeft} /> Back to Petty Cash
                </button>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30 }}>
                {categories.map(cat => (
                    <ConfigPanel
                        key={cat.key}
                        category={cat.key}
                        label={cat.label}
                        placeholder={cat.placeholder}
                        items={data.filter(d => d.category === cat.key)}
                        onRefresh={fetchData}
                    />
                ))}
            </div>
        </div>
    );
}

function ConfigPanel({ category, label, placeholder, items, onRefresh }) {
    const [val, setVal] = useState('');
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editVal, setEditVal] = useState('');
    const errMsg = (err) => err?.response?.data?.message || err?.message || 'Something went wrong';

    const handleAdd = async () => {
        const name = val.trim();
        if (!name || saving) return;
        if (items.some(i => (i.name || '').toLowerCase() === name.toLowerCase())) {
            alert(`"${name}" already exists.`);
            return;
        }
        setSaving(true);
        try {
            await axios.post('/api/master-data', { name, category });
            setVal('');
            onRefresh();
        } catch (err) {
            console.error(err);
            alert('Could not add item: ' + errMsg(err));
        } finally { setSaving(false); }
    };

    const handleUpdate = async (id) => {
        const name = editVal.trim();
        if (!name) return;
        try {
            await axios.put(`/api/master-data/${id}`, { name });
            setEditId(null);
            onRefresh();
        } catch (err) {
            console.error(err);
            alert('Could not update item: ' + errMsg(err));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            await axios.delete(`/api/master-data/${id}`);
            onRefresh();
        } catch (err) {
            console.error(err);
            alert('Could not delete item: ' + errMsg(err));
        }
    };

    return (
        <div className="md-panel">
            <div className="md-panel-header" style={{ padding: '20px 24px' }}>
                <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#64748b' }}>{label}</h3>
            </div>
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                    <input
                        className="md-input"
                        placeholder={placeholder}
                        value={val}
                        onChange={e => setVal(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAdd()}
                    />
                    <button className="premium-btn btn-primary-action" style={{ whiteSpace: 'nowrap' }} onClick={handleAdd} disabled={saving}>
                        {saving ? 'ADDING...' : `ADD ${label.split(' ').pop()}`}
                    </button>
                </div>

                <div className="md-table-wrap" style={{ border: '1px solid #f1f5f9', borderRadius: 12 }}>
                    <table className="md-table">
                        <thead>
                            <tr>
                                <th className="md-th">NAME</th>
                                <th className="md-th" style={{ textAlign: 'right' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr><td colSpan={2} style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>No items added.</td></tr>
                            ) : items.map(item => (
                                <tr key={item._id}>
                                    <td className="md-td" style={{ fontSize: 14, fontWeight: 600 }}>
                                        {editId === item._id ? (
                                            <input className="md-input" autoFocus value={editVal}
                                                onChange={e => setEditVal(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleUpdate(item._id); if (e.key === 'Escape') setEditId(null); }}
                                                onBlur={() => handleUpdate(item._id)}
                                                style={{ height: 34 }} />
                                        ) : item.name}
                                    </td>
                                    <td className="md-td" style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <button className="md-icon-btn" title="Edit" onClick={() => { setEditId(item._id); setEditVal(item.name); }}><Icon d={icons.edit} size={14} /></button>
                                            <button className="md-icon-btn" style={{ color: '#EF4444' }} onClick={() => handleDelete(item._id)}><Icon d={icons.trash} size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
